import {
  ChevronDoubleDownIcon,
  ReceiptPercentIcon,
} from "@heroicons/react/24/solid";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useQueryClient } from "react-query";
import { useRecoilState, useRecoilValue } from "recoil";
import { getPdf } from "../../../../../actions/CarrierAgreementMgmt/VendorInvoice";
import Button2 from "../../../../../components/Button2";
import CloseIconButton from "../../../../../components/CloseIconButton";
import ConfirmBoxModal from "../../../../../components/ConfirmBoxModal";
import { toast } from "../../../../../components/toastNotification";
import { useEditInvoice } from "../../InvoiceCapture/hooks/useEditInvoice";
import { IItemize, IVendorInvoiceDetails } from "../../InvoiceCapture/types";
import { forEdit, forReviewData, isEditStateForReview } from "../atoms/atom";
import { ForReviewForm } from "../forms/ForReviewForms";
import { useRecallInvoiceDetails } from "../hooks/useRecalIInvoice";
import { useDenyInvoiceDetails } from "../hooks/useDenyInvoice";
import { useApprovedInvoiceDetails } from "../hooks/useApprovedInvoice";
import { Disclosure } from "@headlessui/react";
import { FIELDPERMISSIONS } from "../../../../PermissionsConstants";
import { permissionsSelector } from "../../../../Login/selectors/permissionSelector";
import { ForReviewView } from "./ForReviewView";
import { VoidedView } from "./VoidedView";
import { useSubmitToAcumatica } from "../hooks/useSubmitToAcumatica";
import ViewOutlineButton from "../../../../../components/ViewOutlineButton";
import { InvoiceActivityHistoryView } from "../../InvoiceCapture/view/InvoiceActivityHistoryView";
import InvoiceItemizedView from "../../InvoiceCapture/view/InvoiceItemizedView";
import { voidedData } from "../../InvoiceCapture/atoms/atoms";

interface ForReviewDetailsProp {
  cancelShow;
  rowData;
  handleEdit;
  setIsShow;
  activityHistoryData;
}

export const ForReviewDetails: React.FC<ForReviewDetailsProp> = ({
  cancelShow,
  rowData,
  handleEdit,
  setIsShow,
  activityHistoryData,
}) => {
  const [isEdit, setIsEdit] = useRecoilState(isEditStateForReview);
  const [rowForEdit, setRowForEdit] = useRecoilState(forEdit);
  const useFormMethods = useForm<IVendorInvoiceDetails>({
    defaultValues: { ...rowForEdit },
  });
  const queryClient = useQueryClient();
  const [buttonAction] = useState<any>();
  const [openConfirm, setOpenConfirm] = useState(false);
  const [recallOpenModal, setRecallOpenModal] = useState(false);
  const [denyOpenModal, setDenyOpenModal] = useState(false);
  const [approvedOpenModal, setApprovedOpenModal] = useState(false);
  const [id, setId] = useState<any>([]);
  const [forReviewInvoiceDetails, setForReviewInvoiceDetails] =
    useRecoilState<any>(forReviewData);
  const [voidedInvoiceDetails, setVoidedInvoiceDetails] =
    useRecoilState<any>(voidedData);
  const approver = FIELDPERMISSIONS.fieldpermission_vendorinvoice_approver;
  const analyst = FIELDPERMISSIONS.fieldpermission_vendorinvoice_analyst;

  const userPermissions = useRecoilValue(permissionsSelector);

  const { handleSubmit } = useFormMethods;

  const retrieveFile = async (fileName) => {
    try {
      getPdf(fileName).then((content) => {
        const pdfBlob = new Blob([content.data], { type: "application/pdf" });
        const pdfUrl = URL.createObjectURL(pdfBlob);
        window.open(pdfUrl);
      });
    } catch (error) {}
  };
  const onSubmit = (x: IVendorInvoiceDetails) => {
    if (buttonAction === "save") {
      //setNewData({ ...data, vendorInvoiceId: data.id });
      setOpenConfirm(true);
    }
  };

  //Single Edit Initialization
  const {
    mutate: editVendorInvoice,
    isSuccess: editVendorInvoiceSuccess,
    data: editVendorInvoiceResult,
    reset: resetEditVendorInvoiceData,
  } = useEditInvoice();

  //Save edit
  const handleSave = () => {
    const datas = {
      id: rowData?.id,
      vendorInvoiceId: rowData?.vendorInvoiceId,
      status: rowData?.status,
      amountPaid: useFormMethods.getValues("amountPaid"),
      servicePeriodDate: useFormMethods.getValues("servicePeriodDate"),
      invoiceNumber: useFormMethods.getValues("invoiceNumber"),
      invoiceImageAttachment: rowData?.invoiceImageAttachment,
      dateCapture: useFormMethods.getValues("dateCapture"),
      billRefNumber: rowData?.billRefNumber,
      paymentRef: rowData?.paymentRef,
      bankReference: useFormMethods.getValues("bankReference"),
      bankReferenceDate: useFormMethods.getValues("bankReferenceDate")
        ? new Date(useFormMethods.getValues("bankReferenceDate")).toISOString()
        : null,
      noteSection: rowData?.noteSection,
      invoiceAvailabilityDay: rowData?.invoiceAvailabilityDay,
      taxId: useFormMethods.getValues("taxId"),
      isActive: rowData?.isActive,
      recordNumber: rowData?.recordNumber,
      officeId: rowData?.officeId,
      vendorId: rowData?.vendorId,
      createdBy: rowData?.createdBy,
      createdDate: rowData?.createdDate,
      modifiedBy: rowData?.modifiedBy,
      modifiedDate: rowData?.modifiedDate,
      acumaticaVendorId: rowData?.acumaticaVendorId,
      invoiceDetailsId: rowData?.invoiceDetailsId,
    };

    if (datas.bankReference !== null && datas.bankReferenceDate !== null) {
      editVendorInvoice(datas);
    }
  };

  //Toasters
  if (editVendorInvoiceSuccess && editVendorInvoiceResult) {
    if (
      editVendorInvoiceResult.status === 201 ||
      editVendorInvoiceResult.status === 200
    ) {
      toast.fire("Success", "Vendor Invoice updated successfully.", "success");
      setIsEdit(false);
      queryClient.invalidateQueries(["GetForReviewInvoices"]);
    }
    resetEditVendorInvoiceData();
  }

  const handleCancel = () => {
    setIsEdit(false);
    useFormMethods.reset();
    useFormMethods.clearErrors();
    setRowForEdit(undefined);
  };
  const RecallInvoie = useRecallInvoiceDetails();
  const DenyInvoice = useDenyInvoiceDetails();
  const ApprovedInvoice = useApprovedInvoiceDetails();

  const handleRecall = () => {
    RecallInvoie(id);
    setRecallOpenModal(false);
    setIsShow(false);
  };

  const handleDeny = () => {
    DenyInvoice(id);
    setRecallOpenModal(false);
    setIsShow(false);
  };

  const handleApproved = () => {
    ApprovedInvoice(id);
    setRecallOpenModal(false);
    setIsShow(false);
  };

  const invoiceDetails = (row) => {
    const forInvoice = Object.values(row)[2];
    setId([forInvoice]);
  };

  const recallMessage = (
    <div className="text-gray-500 font-normal">{`Are you sure you want to Recall this Invoice Detail ?`}</div>
  );

  const denyMessage = (
    <div className="text-gray-500 font-normal">{`Are you sure you want to Deny this Invoice Detail ?`}</div>
  );

  const approvedMessage = (
    <div className="text-gray-500 font-normal">{`Are you sure you want to Approved this Invoice Detail ?`}</div>
  );

  const {
    mutate: submitInvoiceToAcumatica,
    isSuccess: submitInvoiceToAcumaticaSuccess,
    data: submitInvoiceToAcumaticaResult,
    reset: resetSubmitInvoiceToAcumaticaData,
    isLoading: isLoadingApprove,
  } = useSubmitToAcumatica();

  const approveAndSubmitToAcumatica = () => {
    submitInvoiceToAcumatica(id);
    setApprovedOpenModal(false);
  };
  //Toasters
  useEffect(() => {
    if (submitInvoiceToAcumaticaSuccess && submitInvoiceToAcumaticaResult) {
      if (
        submitInvoiceToAcumaticaResult.status === 201 ||
        submitInvoiceToAcumaticaResult.status === 200
      ) {
        toast.fire(
          "Success",
          "Vendor Invoice updated successfully.",
          "success"
        );
        queryClient.invalidateQueries(["GetForReviewInvoices"]);
        setIsEdit(false);
        setIsShow(false);
      } else {
        toast.fire(
          "Submission failed",
          submitInvoiceToAcumaticaResult?.data,
          "error"
        );
        setApprovedOpenModal(false);
      }
      resetSubmitInvoiceToAcumaticaData();
    }
  }, [submitInvoiceToAcumaticaResult]);

  const calculateFieldSum = (data: IItemize[]) => {
    return data?.reduce((sum, item) => {
      const itemAmount = parseFloat(item.amount?.toString());
      return isNaN(itemAmount) ? sum : sum + itemAmount;
    }, 0);
  };
  return (
    <>
      <div className="box-shadow p-4 border-quinary border-l-2 p-2">
        <div>
          <div className="relative -top-6 -right-4">
            <CloseIconButton
              onClick={() => {
                cancelShow();
                setForReviewInvoiceDetails(undefined);
                setVoidedInvoiceDetails(undefined);
              }}
            />
          </div>
          <div>
            <FormProvider {...useFormMethods}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="flex justify-between items-center border-secondary border-b-2 ">
                  <div>
                    <div className="flex gap-2 p-2 items-center">
                      <div>
                        <ReceiptPercentIcon className="text-white bg-blue-500 rounded-md h-7 w-7 p-1" />
                      </div>
                      <div className="flex flex-col leading tight">
                        <h3 className="text-xs font-medium text-gray-500 ">
                          Vendor Invoice Processing
                        </h3>
                        <h1 className="mr-auto font-bold text-primary text-md uppercase pb-2">
                          Batch List - {rowData?.id}
                        </h1>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="text-xs space-y-2 my-2">
                  <div className="ml-auto py-2 flex rounded box-shadow justify-between ">
                    <div className="space-x-4 mr-6 self-center mx-2 text-pink-500 font-bold">
                      {rowData?.vendorName} - {rowData?.vendorCode}
                    </div>
                    <div className="flex items-center  gap-2">
                      <div className=" ">
                        <div className="flex gap-1 mx-2">
                          <ViewOutlineButton
                            disabled={isLoadingApprove}
                            buttonText={"View PDF"}
                            className={undefined}
                            onClick={() => {
                              retrieveFile(rowData?.invoiceImageAttachment);
                            }}
                          />
                          {userPermissions.includes(analyst) && (
                            <Button2
                              className={
                                isEdit || isLoadingApprove
                                  ? "opacity-50"
                                  : "opacity-100"
                              }
                              disabled={isEdit || isLoadingApprove}
                              buttonText="Recall"
                              onClick={() => {
                                invoiceDetails && invoiceDetails(rowData);
                                setRecallOpenModal(true);
                              }}
                            />
                          )}
                          {userPermissions.includes(approver) && (
                            <>
                              <Button2
                                className={
                                  isEdit || isLoadingApprove
                                    ? "opacity-50"
                                    : "opacity-100"
                                }
                                disabled={isEdit || isLoadingApprove}
                                buttonText="Deny"
                                onClick={() => {
                                  invoiceDetails && invoiceDetails(rowData);
                                  setDenyOpenModal(true);
                                }}
                              />
                              <Button2
                                className={
                                  isEdit || isLoadingApprove
                                    ? "opacity-50"
                                    : "opacity-100"
                                }
                                disabled={isLoadingApprove}
                                buttonText={
                                  isLoadingApprove ? "Loading..." : "Approve"
                                }
                                onClick={() => {
                                  // if (
                                  //   rowData?.bankReferenceDate === null ||
                                  //   rowData?.bankReference === null
                                  // ) {
                                  //   toast.fire(
                                  //     "Information",
                                  //     "Bank Reference Date and Bank Reference are required to submit to acumatica.",
                                  //     "info"
                                  //   );
                                  // } else
                                  // const pattern = /^\d{2}-\d{3}-\d{6}-\d{2}$/;
                                  // if (
                                  //   rowData?.glsubaccounts === null ||
                                  //   rowData?.glsubaccounts === ""
                                  // ) {
                                  //   toast.fire(
                                  //     "Information",
                                  //     "GL/Subaccounts is required to submit to acumatica. Kindly go to Office Vendor Service to update the field",
                                  //     "info"
                                  //   );
                                  // } else if (
                                  //   !pattern.test(rowData?.glsubaccounts)
                                  // ) {
                                  //   toast.fire(
                                  //     "Information",
                                  //     "Invalid Subaccounts format. Kindly go to Office Vendor Service to update the field",
                                  //     "info"
                                  //   );
                                  // } else
                                  if (
                                    rowData?.vendorCode === null ||
                                    rowData?.vendorCode === ""
                                  ) {
                                    toast.fire(
                                      "Information",
                                      "Vendor ID is required to submit to acumatica. Kindly go to Vendor to update the field",
                                      "info"
                                    );
                                  } else {
                                    invoiceDetails && invoiceDetails(rowData);
                                    setApprovedOpenModal(true);
                                  }
                                }}
                              />
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {isEdit && (
                  <div>
                    <div className="w-full">
                      <div className="">
                        <div className="mt-2 bg-white box-shadow rounded-md">
                          <div className="px-4 text-quinary py-2 font-bold">
                            {" Invoice Details 1 - For Review"}
                          </div>
                          <div></div>
                          <div>
                            <ForReviewForm
                              rowForEdit={rowForEdit}
                              isEditState={isEdit}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {!isEdit && (
                  <div className="w-full">
                    <div className="mt-2 bg-white p-4 box-shadow rounded-md ">
                      <div className="text-quinary pb-4 font-bold ">
                        Vendor Information
                      </div>
                      <div className="grid grid-cols-3 items-center">
                        <div className="self-start">
                          <div className="expandedLabelVIP fontExpand ml-2">
                            {rowData?.vendorName}
                          </div>
                          <div>
                            <div className="expandedDataVIP fontExpand ml-2">
                              {rowData?.vendorCode}
                            </div>
                          </div>
                        </div>
                        <div className="">
                          <div className="expandedLabelVIP fontExpand ml-4">
                            {rowData?.officeName}
                          </div>
                          <div className="expandedLabelVIP fontExpand ml-4">
                            {/* {rowData?.officeAddress} {", "}
                            {rowData?.officeStateCode} */}
                            {rowData?.officeStreet}, {rowData?.officeCity},{" "}
                            {rowData?.officeStateCode}
                          </div>
                          <div className="expandedLabelVIP fontExpand ml-4">
                            {rowData?.officeZipcode}
                          </div>
                        </div>
                        <div className="self-start">
                          <div className="expandedLabelVIP fontExpand ml-4">
                            Vendor ID: {rowData?.vendorCode}
                          </div>
                          <div className="expandedLabelVIP fontExpand ml-4">
                            Address Code: {rowData?.vendorCode}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className=" mt-2 bg-white p-4 box-shadow rounded-md  ">
                      <div className="flex flex-row justify-between">
                        <div className="font-bold text-quinary ">
                          Invoice Details{" "}
                          {forReviewInvoiceDetails
                            ? forReviewInvoiceDetails?.length + 1
                            : 1}{" "}
                          - For Review
                        </div>
                      </div>
                      <ForReviewView rowData={rowData} />
                    </div>
                  </div>
                )}
                <div className={""}>
                  {voidedInvoiceDetails &&
                    voidedInvoiceDetails?.map((data, index) => {
                      return (
                        <>
                          <Disclosure>
                            {({ open }) => (
                              <>
                                <div className="mt-2 bg-white rounded-lg box-shadow ">
                                  <div className="flex flex-row justify-between">
                                    <div className="text-quinary font-bold font-sans py-2 px-3">
                                      Invoice Details {index + 1} - Voided
                                    </div>
                                    <div>
                                      <Disclosure.Button className="disclosureBtn ml-2 justify-end">
                                        <ChevronDoubleDownIcon
                                          className={`${
                                            open ? "transform rotate-180" : ""
                                          }  text-gray-500 w-5 h-5 mr-4`}
                                        />
                                      </Disclosure.Button>
                                    </div>
                                  </div>
                                  <Disclosure.Panel className="text-xxs">
                                    <div className="">
                                      <VoidedView rowData={data} />
                                    </div>
                                  </Disclosure.Panel>
                                </div>
                              </>
                            )}
                          </Disclosure>
                        </>
                      );
                    })}
                </div>
                {/* Allocation Section */}
                <div className="w-full bg-white">
                  <Disclosure defaultOpen={true}>
                    {({ open }) => (
                      <>
                        <div className=" bg-white rounded-lg box-shadow">
                          <div className="flex flex-row justify-between mt-2">
                            <div className="text-quinary font-bold font-sans py-2 px-3">
                              Allocation Section
                            </div>
                            <div>
                              <Disclosure.Button className="disclosureBtn ml-2 justify-end">
                                <ChevronDoubleDownIcon
                                  className={`${
                                    open ? "transform rotate-180" : ""
                                  }  text-gray-500 w-5 h-5 mr-4`}
                                />
                              </Disclosure.Button>
                            </div>
                          </div>
                          <Disclosure.Panel className="text-xxs">
                            <InvoiceItemizedView
                              rowData={
                                rowData?.itemizeDetails?.length > 0
                                  ? rowData?.itemizeDetails
                                  : []
                              }
                            />
                          </Disclosure.Panel>
                        </div>
                      </>
                    )}
                  </Disclosure>
                </div>
                <div className="w-full bg-white">
                  <Disclosure>
                    {({ open }) => (
                      <>
                        <div className="bg-white rounded-lg box-shadow">
                          <div className="flex flex-row justify-between mt-2">
                            <div className="text-quinary font-bold font-sans py-2 px-3">
                              Activity History
                            </div>
                            <div>
                              <Disclosure.Button className="disclosureBtn ml-2 justify-end">
                                <ChevronDoubleDownIcon
                                  className={`${
                                    open ? "transform rotate-180" : ""
                                  }  text-gray-500 w-5 h-5 mr-4`}
                                />
                              </Disclosure.Button>
                            </div>
                          </div>
                          <Disclosure.Panel className="text-xxs">
                            <InvoiceActivityHistoryView
                              activityHistoryData={activityHistoryData}
                            />
                          </Disclosure.Panel>
                        </div>
                      </>
                    )}
                  </Disclosure>
                </div>
              </form>
            </FormProvider>
          </div>
        </div>
      </div>
      {/* Confirmation modal */}
      {openConfirm && (
        <ConfirmBoxModal
          qConfirmation={
            buttonAction === "save"
              ? "Are you sure you want to update this invoice?"
              : "Are you sure you want to submit this invoice?"
          }
          isOpen={openConfirm}
          onClose={() => setOpenConfirm(false)}
          cancelButtonRef={undefined}
          onClickFunction={undefined}
          ModalTitle={undefined}
        />
      )}
      {recallOpenModal && (
        <ConfirmBoxModal
          qConfirmation={recallMessage}
          isOpen={recallOpenModal}
          onClose={() => setRecallOpenModal(false)}
          onClickFunction={handleRecall}
          cancelButtonRef={undefined}
          ModalTitle={"Recall Invoice Details"}
        />
      )}
      {denyOpenModal && (
        <ConfirmBoxModal
          qConfirmation={denyMessage}
          isOpen={denyOpenModal}
          onClose={() => setDenyOpenModal(false)}
          onClickFunction={handleDeny}
          cancelButtonRef={undefined}
          ModalTitle={"Deny Invoice Details"}
        />
      )}
      {approvedOpenModal && (
        <ConfirmBoxModal
          qConfirmation={approvedMessage}
          isOpen={approvedOpenModal}
          onClose={() => setApprovedOpenModal(false)}
          onClickFunction={approveAndSubmitToAcumatica}
          cancelButtonRef={undefined}
          ModalTitle={"Approve Invoice Details"}
        />
      )}
    </>
  );
};
